/**
 * Ground the gas-BTM fuel cost from a natural-gas price (v1.2.1 HR-5 P2-4).
 *
 * The audit's core deliverable: gas LCOE must be GROUNDED, not assumed. This
 * helper converts a provider-grounded $/MMBtu price into $/MWh fuel cost using
 * a NAMED heat-rate constant (engineering assumption, flagged), and applies it
 * to `FinancialAssumptions.fuelCostPerMwhUsd` with strict USER-PRECEDENCE:
 *
 *   user-declared  >  provider-grounded  >  assumed/default
 *
 * It NEVER overwrites a user-entered value (precedence-incident memory). When
 * it does apply a value it stamps per-field provenance so the LCOE form chip
 * shows the EIA source.
 *
 * UNITS (dimensional analysis):
 *   fuelCost [$/MWh] = price [$/MMBtu] × heatRate [Btu/kWh] × (1 MMBtu / 1e6 Btu)
 *                       × (1000 kWh / 1 MWh)
 *                    = price × heatRate / 1000
 *   e.g. $4.00/MMBtu × 9000 Btu/kWh / 1000 = $36.0/MWh.  ✓
 */
import type {
  FinancialAssumptions,
  FieldProvenance,
} from '../types/financialAssumptions';
import type { ProjectIntent } from '../types/siteRag';

/**
 * Default genset/turbine heat rate, Btu/kWh, used ONLY when no genset heat rate
 * is declared. ~9,000 Btu/kWh ≈ a modern simple-cycle gas turbine; combined-
 * cycle is lower (~6,400) — this is a CONSERVATIVE simple-cycle assumption.
 * NAMED constant — NOT a magic number (CLAUDE.md rule 4/5). The caller surfaces
 * the heat rate on the fuelSupply section so the assumption is auditable.
 */
export const DEFAULT_GAS_HEAT_RATE_BTU_PER_KWH = 9000;

/**
 * Convert a natural-gas price ($/MMBtu) + heat rate (Btu/kWh) into a fuel cost
 * ($/MWh). Returns null when either input is missing / non-finite / non-positive
 * (never fabricate a fuel cost).
 */
export function gasFuelCostPerMwhUsd(
  priceUsdPerMmbtu: number | null | undefined,
  heatRateBtuPerKwh: number | null | undefined = DEFAULT_GAS_HEAT_RATE_BTU_PER_KWH,
): number | null {
  if (priceUsdPerMmbtu == null || !Number.isFinite(priceUsdPerMmbtu) || priceUsdPerMmbtu <= 0) {
    return null;
  }
  const hr = heatRateBtuPerKwh ?? DEFAULT_GAS_HEAT_RATE_BTU_PER_KWH;
  if (!Number.isFinite(hr) || hr <= 0) return null;
  // $/MWh = $/MMBtu × Btu/kWh / 1000.
  return (priceUsdPerMmbtu * hr) / 1000;
}

/** A gas pathway is in scope when the financial technology is a gas variant. */
export function isGasTechnology(tech: FinancialAssumptions['technology'] | undefined): boolean {
  return tech === 'gas-cc' || tech === 'gas-sc';
}

/**
 * True when a project intent involves natural gas anywhere in its power mix —
 * a top-level / phase `powerSource` of gas-cc / gas-sc / hybrid, OR a granular
 * phase component (recip / gas-turbine). Used to GATE the fuelSupply
 * completeness / gap surfaces so a nuclear- or renewables-only intent is NOT
 * penalised for a missing gas price (field-architecture-audit lesson:
 * irrelevant-axis weighting). A `hybrid` phase is treated as gas-relevant
 * conservatively (it MAY include gas) so a gas price is still surfaced as
 * useful context rather than hidden.
 */
export function intentHasGasPathway(intent: ProjectIntent | null | undefined): boolean {
  if (!intent) return false;
  const gasSourceKeys = new Set(['gas-cc', 'gas-sc', 'hybrid']);
  const gasComponentKeys = new Set(['recip', 'gas-turbine']);
  const phases = intent.phases ?? [];
  for (const p of phases) {
    if (p.powerSource && gasSourceKeys.has(p.powerSource)) return true;
    const components = p.powerSourceConfig?.components ?? [];
    for (const c of components) {
      if (gasComponentKeys.has(c)) return true;
    }
  }
  return false;
}

export interface ApplyGroundedGasFuelCostResult {
  /** True when the grounded value was written onto the assumptions. */
  applied: boolean;
  /** Why it was / wasn't applied — for audit + the LcoeEvidence basis. */
  reason:
    | 'applied-provider-grounded'
    | 'skipped-user-declared'
    | 'skipped-not-gas'
    | 'skipped-no-price';
  /** The resolved fuel cost ($/MWh) that is (or already was) in effect. */
  fuelCostPerMwhUsd: number | null;
}

/**
 * Apply a provider-grounded gas fuel cost to a FinancialAssumptions object
 * IN PLACE (returns the same object), with USER-PRECEDENCE:
 *
 *   - Not a gas pathway          → no-op  ('skipped-not-gas').
 *   - User already declared a
 *     fuelCostPerMwhUsd (provenance source !== 'industry-baseline'/'derived')
 *                                → no-op  ('skipped-user-declared').
 *   - No grounded price          → no-op  ('skipped-no-price').
 *   - Otherwise                  → write grounded value + provenance.
 *
 * "User-declared" = a finite fuelCostPerMwhUsd whose `fieldProvenance.source`
 * is `manual` or an `extracted-*` source. A value previously written by THIS
 * helper (source `industry-baseline`, baselineRef starting with 'EIA') is NOT
 * treated as user-declared, so a fresh provider value can refresh it.
 */
export function applyGroundedGasFuelCost(
  assumptions: FinancialAssumptions,
  priceUsdPerMmbtu: number | null | undefined,
  opts: {
    heatRateBtuPerKwh?: number | null;
    state?: string | null;
    period?: string | null;
    priceSeries?: string | null;
  } = {},
): ApplyGroundedGasFuelCostResult {
  if (!isGasTechnology(assumptions.technology)) {
    return { applied: false, reason: 'skipped-not-gas', fuelCostPerMwhUsd: assumptions.fuelCostPerMwhUsd };
  }

  // User-precedence: a manually-entered / extracted fuel cost always wins.
  const existing = assumptions.fuelCostPerMwhUsd;
  const existingProv = assumptions.fieldProvenance?.fuelCostPerMwhUsd;
  const userDeclared =
    existing != null &&
    Number.isFinite(existing) &&
    existingProv != null &&
    existingProv.source !== 'industry-baseline' &&
    existingProv.source !== 'derived';
  if (userDeclared) {
    return { applied: false, reason: 'skipped-user-declared', fuelCostPerMwhUsd: existing };
  }

  const grounded = gasFuelCostPerMwhUsd(priceUsdPerMmbtu, opts.heatRateBtuPerKwh);
  if (grounded == null) {
    return { applied: false, reason: 'skipped-no-price', fuelCostPerMwhUsd: existing };
  }

  const baselineRef =
    `EIA ${opts.priceSeries ?? 'natural-gas'}` +
    `${opts.state ? ` ${opts.state}` : ''}` +
    `${opts.period ? ` ${opts.period}` : ''}`;

  const provenance: FieldProvenance = {
    source: 'industry-baseline',
    confidence: opts.priceSeries === 'henry-hub-fallback' ? 'medium' : 'high',
    baselineRef,
    extractedAt: new Date().toISOString(),
  };

  assumptions.fuelCostPerMwhUsd = grounded;
  assumptions.fieldProvenance = {
    ...(assumptions.fieldProvenance ?? {}),
    fuelCostPerMwhUsd: provenance,
  };

  return { applied: true, reason: 'applied-provider-grounded', fuelCostPerMwhUsd: grounded };
}
